import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2 } from "lucide-react";
import { ci, fmt, STATUS_STYLE, setCountryConfigs } from "./api";
import { CountrySettings } from "./CountrySettings";
import { InvoiceForm } from "./InvoiceForm";
import { InvoiceDetail } from "./InvoiceDetail";

const VIEWS = [["drafts", "Draft Invoices", "DRAFT,RETURNED"], ["pending", "Pending Approval", "PENDING_APPROVAL"], ["approved", "Approved Invoices", "APPROVED"], ["history", "Invoice History", ""]];

function BankDetails({ token, onSaved }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});
  const load = useCallback(async () => {
    const r = await ci("bank/list", token);
    setData(r);
    setRows(Object.fromEntries(r.banks.map((b) => [b.center, b])));
  }, [token]);
  useEffect(() => { load().catch((e) => toast.error(e.message)); }, [load]);
  if (!data) return <Loader2 className="animate-spin" />;
  const save = async (code) => {
    const reason = window.prompt(`Verification note for ${code} bank / tax registration details:`);
    if (!reason) return;
    try { const r = await ci("bank/save", token, { center: code, details: rows[code] || {}, reason }); toast.success(r.missing.length ? `Saved — still missing: ${r.missing.join(", ")}` : "Saved — complete"); load(); onSaved(); }
    catch (e) { toast.error(e.message); }
  };
  const set = (code, k, v) => setRows({ ...rows, [code]: { ...(rows[code] || {}), [k]: v } });
  return (
    <div className="space-y-3" data-testid="ci-bank-details">
      <p className="text-xs text-slate-600">Super Admin only. Each centre uses only its own legal entity and bank account — no fallback or copying. Fields follow the centre's country (India: GSTIN + IFSC · Australia: ABN + BSB). Required fields (*) must be verified before invoices can be approved.</p>
      {data.centres.map((c) => {
        const cfg = data.countries[c.country];
        if (!cfg) return <p key={c.code} className="text-xs text-red-700" data-testid={`ci-bank-${c.code}`}>{c.code}: no country settings for {c.country} — add them under Country Settings.</p>;
        return (
          <div key={c.code} className="rounded border p-2" data-testid={`ci-bank-${c.code}`}>
            <p className="text-xs font-semibold">{c.code} — {c.name} <span className="font-normal text-slate-500">· {c.country} · {cfg.currency_code} · {cfg.tax_reg_label}</span></p>
            {[["Legal entity & tax", cfg.entity_fields], ["Bank", cfg.bank_fields]].map(([title, fs]) => (
              <div key={title} className="mt-1"><p className="text-[10px] uppercase text-slate-400">{title}</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-1">{fs.map((f) => (
                  <label key={f.key} className="text-[10px] text-slate-600">{f.label}{f.required ? " *" : " (optional)"}
                    <Input className="h-8 text-xs" value={rows[c.code]?.[f.key] || ""} onChange={(e) => set(c.code, f.key, e.target.value)} data-testid={`ci-bank-${c.code}-${f.key}`} /></label>))}</div></div>))}
            <Button size="sm" className="mt-1 h-7" onClick={() => save(c.code)} data-testid={`ci-bank-save-${c.code}`}>Save & verify</Button>
          </div>);
      })}
    </div>
  );
}

export function CorporateInvoicesTab({ token }) {
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState("drafts");
  const [list, setList] = useState([]);
  const [openId, setOpenId] = useState(() => new URLSearchParams(window.location.search).get("invoice"));
  const loadList = useCallback(async () => {
    const v = VIEWS.find((x) => x[0] === view);
    if (!v) return;
    try { setList((await ci("list", token, { status: v[2] || null })).invoices); } catch (e) { toast.error(e.message); }
  }, [token, view]);
  const loadMeta = useCallback(() => ci("meta", token).then((m) => { setCountryConfigs(m.countries); setMeta(m); }).catch((e) => toast.error(e.message)), [token]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadList(); }, [loadList]);
  if (!meta) return <div className="py-8 text-center"><Loader2 className="inline animate-spin" /></div>;
  const p = meta.permissions;
  return (
    <div className="space-y-3" data-testid="corporate-invoices-tab">
      <div className="flex flex-wrap gap-2">
        {p.can_create && <Button size="sm" onClick={() => { setView("new"); setOpenId(null); }} data-testid="ci-new-btn">New Invoice</Button>}
        {VIEWS.map(([k, l]) => <Button key={k} size="sm" variant={view === k ? "default" : "outline"} onClick={() => { setView(k); setOpenId(null); }} data-testid={`ci-view-${k}`}>{l}</Button>)}
        {p.is_super_admin && <Button size="sm" variant={view === "bank" ? "default" : "outline"} onClick={() => { setView("bank"); setOpenId(null); }} data-testid="ci-view-bank">Centre Bank Details</Button>}
        {p.is_super_admin && <Button size="sm" variant={view === "countries" ? "default" : "outline"} onClick={() => { setView("countries"); setOpenId(null); }} data-testid="ci-view-countries">Country Settings</Button>}
        {p.is_super_admin && <Button size="sm" variant="ghost" onClick={() => ci("email-test", token).then((r) => toast[r.sent.every(Boolean) ? "success" : "error"](`Email test: ${r.sent.filter(Boolean).length}/${r.recipients} delivered ${r.failure.filter(Boolean).join(", ")}`)).catch((e) => toast.error(e.message))} data-testid="ci-email-test-btn">Test approver email</Button>}
      </div>
      <p className="text-[11px] text-slate-500">Approver: {meta.approvers.map((a) => a.name).join(", ") || "not configured"} · approval needs the approver's login + email OTP · PDF available after approval.</p>
      {openId ? <InvoiceDetail id={openId} token={token} meta={meta} onBack={() => setOpenId(null)} onChanged={loadList} />
        : view === "new" ? <InvoiceForm token={token} meta={meta} onCancel={() => setView("drafts")} onSaved={(r) => { setView("drafts"); setOpenId(r.id); loadList(); }} />
        : view === "bank" ? <BankDetails token={token} onSaved={loadMeta} />
        : view === "countries" ? <CountrySettings token={token} onSaved={loadMeta} />
        : (
          <table className="w-full rounded border bg-white text-xs" data-testid="ci-list">
            <thead className="bg-slate-50"><tr><th className="p-2 text-left">Reference</th><th className="p-2 text-left">Centre</th><th className="p-2 text-left">Customer</th><th className="p-2">Date</th><th className="p-2 text-right">Total</th><th className="p-2">Status</th><th className="p-2">Payment</th></tr></thead>
            <tbody>{list.map((i) => (
              <tr key={i.id} className="cursor-pointer border-t hover:bg-orange-50" onClick={() => setOpenId(i.id)} data-testid={`ci-row-${i.id}`}>
                <td className="p-2 font-medium">{i.invoice_number || i.draft_ref}</td><td className="p-2">{i.center}</td><td className="p-2">{i.customer?.company_name}</td>
                <td className="p-2 text-center">{i.invoice_date}</td><td className="p-2 text-right">{fmt(i.totals?.invoice_total, i.country)}</td>
                <td className="p-2 text-center"><span className={`rounded px-1.5 py-0.5 ${STATUS_STYLE[i.status] || ""}`}>{i.status.replace(/_/g, " ")}</span></td>
                <td className="p-2 text-center">{i.payment_status?.replace("_", " ") || "—"}</td>
              </tr>))}
              {!list.length && <tr><td colSpan={7} className="p-4 text-center text-slate-500" data-testid="ci-list-empty">No invoices.</td></tr>}</tbody>
          </table>)}
    </div>
  );
}
